import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { EmptyCard } from '..'
import { timeframeOptions } from '../../constants'
import { usePairChartData } from '../../contexts/PairData'
import { TYPE } from '../../Theme'
import { formattedNum, getTimeframe, toNiceDate, toNiceDateYear } from '../../utils'
import VolumeChart from './VolumeChart'

const ChartContainer = styled.div`
  background: #1b1c21;
  border-radius: 16px;
  width: 100%;
  height: 481px;
  position: relative;
  flex-shrink: 0;

  @media screen and (max-width: 1024px) {
    width: 100%;
    height: 400px;
  }

  @media screen and (max-width: 768px) {
    height: 350px;
  }
`

const ChartFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;

  @media screen and (max-width: 768px) {
    gap: 2px;
  }
`

const ChartMetricFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;

  @media screen and (max-width: 768px) {
    gap: 2px;
  }
`

const FilterButton = styled.div`
  backdrop-filter: blur(16px);
  background: ${({ active }) => (active ? '#222328' : 'rgba(179, 179, 190, 0)')};
  border-radius: 32px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: white;
  line-height: 1.4;
  white-space: nowrap;
  transition: all 0.2s ease;

  @media screen and (max-width: 768px) {
    padding: 8px 12px;
    font-size: 12px;
    border-radius: 20px;
  }

  @media screen and (max-width: 480px) {
    padding: 6px 10px;
    font-size: 11px;
  }

  &:hover {
    background: ${({ active }) => (active ? '#222328' : 'rgba(255, 255, 255, 0.05)')};
  }
`

const ChartValue = styled.div`
  padding: 20px 37px;
  font-family: 'Inter Display', sans-serif;
  font-weight: 700;
  font-size: 32px;
  color: white;
  line-height: 1;

  @media screen and (max-width: 768px) {
    padding: 16px 20px;
    font-size: 24px;
  }

  @media screen and (max-width: 480px) {
    padding: 12px 16px;
    font-size: 20px;
  }
`

const ChartArea = styled.div`
  height: 370px;

  @media screen and (max-width: 1024px) {
    height: 300px;
  }

  @media screen and (max-width: 768px) {
    height: 250px;
  }
`

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 26px 20px 0;

  @media screen and (max-width: 768px) {
    padding: 16px 12px 0;
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  @media screen and (max-width: 480px) {
    padding: 12px 8px 0;
    gap: 8px;
  }
`

const CHART_VIEW = {
  VOLUME: 'Volume',
  LIQUIDITY: 'Liquidity',
  TVL: 'TVL',
  PRICE: 'Price',
}

const PairChart = ({ address, color = '#B3B3BE' }) => {
  const [chartFilter, setChartFilter] = useState(CHART_VIEW.VOLUME)
  const [timeWindow, setTimeWindow] = useState(timeframeOptions.MONTH)

  const timeFilters = ['1W', '1M', 'ALL']
  const metricFilters = ['Volume', 'TVL', 'Price']

  // get data for pair
  let chartData = usePairChartData(address)
  const [latestValue, setLatestValue] = useState()

  let utcStartTime = getTimeframe(timeWindow)
  chartData = chartData?.filter((entry) => entry.date >= utcStartTime)

  // Process chart data to ensure proper date formatting
  if (chartData) {
    chartData = chartData.map((entry) => ({
      ...entry,
      // Convert timestamp to date string for category axis
      date: toNiceDate(entry.date),
      // Keep original timestamp for tooltip
      timestamp: entry.date,
    }))
  }

  // Debug: Log chart data to understand structure
  console.log('Chart data:', chartData)
  console.log('Chart filter:', chartFilter)
  if (chartData && chartData.length > 0) {
    console.log('Sample data point:', chartData[0])
  }

  if (chartData && chartData.length === 0) {
    return (
      <ChartContainer>
        <EmptyCard height="300px">
          <TYPE.body1>No historical data yet</TYPE.body1>
        </EmptyCard>
      </ChartContainer>
    )
  }

  // Update latest value based on chart filter
  const updateLatestValue = () => {
    if (chartData && chartData.length > 0) {
      const latestData = chartData[chartData.length - 1]
      if (chartFilter === CHART_VIEW.VOLUME) {
        return formattedNum(latestData.dailyVolumeUSD, true)
      } else if (chartFilter === CHART_VIEW.TVL) {
        return formattedNum(latestData.reserveUSD, true)
      }
    }
    return '$32.447'
  }

  const getTimeWindowValue = (filter) => {
    switch (filter) {
      case '1W':
        return timeframeOptions.WEEK
      case '1M':
        return timeframeOptions.MONTH
      case 'ALL':
        return timeframeOptions.ALL_TIME
      default:
        return timeframeOptions.MONTH
    }
  }

  const renderChart = () => {
    if (chartFilter === CHART_VIEW.TVL) {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 20, right: 20, bottom: 40, left: 40 }}>
            <defs>
              <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.35} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#ffffff', opacity: 0.8 }}
              type="category"
              interval="preserveStartEnd"
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#ffffff', opacity: 0.8 }}
              orientation="right"
              tickFormatter={(value) => {
                if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
                if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
                return value.toString()
              }}
            />
            <Tooltip
              cursor={true}
              formatter={(val) => {
                setLatestValue(formattedNum(val, true))
                return formattedNum(val, true)
              }}
              labelFormatter={(label) => {
                // Find the data point to get the original timestamp
                const dataPoint = chartData?.find((d) => d.date === label)
                return toNiceDateYear(dataPoint?.timestamp || label)
              }}
              contentStyle={{
                padding: '10px 14px',
                borderRadius: 10,
                borderColor: color,
                color: 'black',
              }}
            />
            <Area
              strokeWidth={2}
              dot={false}
              type="monotone"
              dataKey="reserveUSD"
              stroke={color}
              fill="url(#colorUv)"
            />
          </AreaChart>
        </ResponsiveContainer>
      )
    }

    if (chartFilter === CHART_VIEW.VOLUME) {
      return <VolumeChart chartData={chartData} setLatestValue={setLatestValue} />
    }

    if (chartFilter === CHART_VIEW.PRICE) {
      return (
        <iframe
          title="DEXTswap Aggregator"
          width="100%"
          height="100%"
          src={`https://www.dextools.io/widget-chart/en/polygon/pe-light/${address}?theme=dark`}
          style={{ border: 'none', backgroundColor: 'transparent' }}
        />
      )
    }

    return null
  }

  return (
    <ChartContainer>
      <ChartHeader>
        <ChartFilters>
          {timeFilters.map((filter) => (
            <FilterButton
              key={filter}
              active={getTimeWindowValue(filter) === timeWindow}
              onClick={() => setTimeWindow(getTimeWindowValue(filter))}
            >
              {filter}
            </FilterButton>
          ))}
        </ChartFilters>

        <ChartMetricFilters>
          {metricFilters.map((filter) => (
            <FilterButton key={filter} active={chartFilter === filter} onClick={() => setChartFilter(filter)}>
              {filter}
            </FilterButton>
          ))}
        </ChartMetricFilters>
      </ChartHeader>

      <ChartValue>{latestValue || updateLatestValue()}</ChartValue>

      <div>
        <ChartArea>{renderChart()}</ChartArea>
      </div>
    </ChartContainer>
  )
}

PairChart.propTypes = {
  address: PropTypes.string.isRequired,
  color: PropTypes.string,
}

export default PairChart
